import React, { FC, useEffect, useState, useMemo, useCallback, cloneElement } from 'react';
import { useFormik } from 'formik';
import { AxiosResponse } from 'axios';
import { get } from 'lodash';
import {
  SpheraModal,
  SpheraNotificationGroup,
  SpheraNotification,
  SpheraDropdown,
  SpheraInput, SpheraSpinner,
} from '@sphera-cloud/component-ui-reactjs';
import {
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { Grid, GridSortChangeEvent, ISortDescriptor, LinkToRepo } from 'lca-common-ui-components';

import { File, FilesData } from 'src/components/FilesList/types';
import { useSpinnerContext } from 'src/context/SpinnerContext';
import { fetchFiles, filesSelector, isLoadingSelector } from 'src/store/slices/files';
import { JobSpec } from 'src/store/slices/jobSpec/types';
import { createJob } from 'src/store/slices/jobs';

import { jobSpecSelector } from 'src/store/slices/jobSpec';
import { useAppDispatch, useAppSelector } from 'src/hooks';

import { CreateJobInput, Job, CreateJobObj } from 'src/store/slices/jobs/types';
import { fileSpecSelector } from 'src/store/slices/fileSpec';
import filesApi from '../../store/slices/files/api';

import { infoSelector } from 'src/store/slices/info';
import { AuthHelper } from 'src/components/Auth/AuthHelper';

import './styles.scss';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import classNames from 'classnames';
import { TruncateText } from 'src/components/TruncateText';
import { useUserSettings } from 'src/hooks/useUserSettings';
import { ITEMS_PER_PAGE, PAGE_OPTIONS } from 'src/helpers/Constants';
import { Link } from 'react-router-dom';

interface Props {
  onClose: () => void;
  onCreate: (input: CreateJobInput) => void;
  showNotification: (type: string, message: string) => void;
  hideNotification: () => void;
  job: Job | null;
}

const BOM_JOB_TYPES = new Set([
  'BOM-LCA',
  'BOM-LCA-EF-3.1',
  'BOM-LCA-EN15804+A2',
  'BOM-LCA-TRACI-2.1',
  'BOM-LCA-CML-2001-Aug2016',
]);

const DS_IMPORT_TSX_TASK = 'ds-import-tsx';
const REPOSITORY_COMMIT_FILE_KEY = 'repositoryCommitInfo';

const initialPageOptions = {
  skip: 0,
  take: ITEMS_PER_PAGE,
};

const initialSort: Array<ISortDescriptor> = [{ field: '', dir: 'asc' }];

const defaultNotification = { isOpen: false, type: '', text: '' };
const BASIC_WIDTH = 1000;
const BASIC_HEIGHT = 900;

const CreateJobModal: FC<Props> = ({ onClose, showNotification, hideNotification, job }) => {
  const dispatch = useAppDispatch();

  const { setShowSpinner } = useSpinnerContext();

  const [isInitialized, setIsInitialized] = useState(false);
  const [height, setHeight] = useState<number | string>(BASIC_HEIGHT);
  const [width, setWidth] = useState<number | string>(BASIC_WIDTH);
  const [pageOptions, setPageOptions] = useState<{ skip: number; take: number }>(
    initialPageOptions,
  );
  const [sort, setSort] = useState(initialSort);
  const [maximize, setMaximize] = useState<boolean>(true);
  const [minimize, setMinimize] = useState<boolean>(false);
  const [actionDisabled, setActionDisabled] = useState<boolean>(true);
  const [notification, setNotification] = useState(defaultNotification);
  const files = useAppSelector(filesSelector);
  const jobSpec = useAppSelector(jobSpecSelector);
  const jobSpecUi = jobSpec.filter(spec => spec.tags.find(tag => tag === 'ui'));
  const fileSpec = useAppSelector(fileSpecSelector);
  const [selectedState, setSelectedState] = useState<
    Record<string, { fileId: string; mimeType: string; name: string }>
  >({});

  const info = useAppSelector(infoSelector);
  const [selectedMimeType, setSelectedMimeType] = useState<string | null>(null);
  const [showLinkToDatabaseModal, setShowLinkToDatabaseModal] = useState<boolean>(false);
  const [jsonOutput, setJsonOutput] = useState<string>('');
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: "and",
    filters: [],
  });
  const [selectedFileMimeTypes, setSelectedFileMimeTypes] = useState<string[]>([]);
  const { getFormattedDateTime } = useUserSettings();

  const dataServer = info.urls.find((item) => item.target === 'DataServer');

  let mimetypesData: Array<{
    name: string;
    fileKey: string;
    purpose: string;
    actions: Array<{
      text: string;
      onClick: (m: any) => void;
      icon: string;
      visible: boolean;
    }>;
  }> = [];

  const formik = useFormik<{
    name: string;
    jobType: JobSpec | null;
  }>({
    enableReinitialize: true,
    initialValues: {
      name: '',
      jobType: null,
    },
    validateOnChange: false,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name) errors.name = 'Job name is required';
      if (!values.jobType) errors.jobType = 'Job type is required';
      return errors;
    },
    onSubmit: async (values) => {
      if (!values.jobType) return;
      setActionDisabled(true);
      setSelectedFileMimeTypes([]);
      const fileIds = Object.keys(selectedState);
      const jobType = values.jobType;
      const files: CreateJobObj[] = [];
      fileIds.forEach((id) => {
        const mimeType = selectedState[id].mimeType;
        const { fileType } = jobType.inputs.find((jobType) => jobType.mimeType === mimeType) ?? {};
        if (fileType) {
          files.push({
            fileType: fileType,
            fileId: id,
          });
        }
      });
      setShowSpinner(true);

      const parameters = jsonOutput.length ? JSON.parse(jsonOutput) : { ...(job?.parameters ?? {}) };

      if (parameters.repositoryKey && parameters.commitKey && BOM_JOB_TYPES.has(values.jobType.id)) {
        parameters.includeOptionalTask = DS_IMPORT_TSX_TASK;
      }

      const response = await dispatch(
        createJob({
          name: values.name,
          type: values.jobType.id,
          files,
          parameters,
        }),
      );
      if (response.type.includes('rejected')) {
        setShowSpinner(false);
        setActionDisabled(false);
      } else {
        showNotification('success', 'Job created successfully');
        setIsInitialized(false);
        onClose();
        hideNotification();
      }
    },
  });

  const isLoading = useAppSelector(isLoadingSelector);

  const initJob = useCallback(
    async (job: Job) => {
      const jobTypeId = job.type;
      const jobType = jobSpec.find((js) => js.id === jobTypeId) || null;
      const fileIds: string[] = [];
      (jobType?.inputs || []).forEach(({ fileType }) => {
        const file = (job?.files || []).find((item) => item.fileType === fileType);
        if (file) {
          fileIds.push(file.fileId);
        }
      });

      let fileResponse: AxiosResponse<FilesData, any> | null = null;
      try {
        fileResponse = await filesApi.fetchFiles({ ids: `["${fileIds.join('","')}"]` });
      } catch (err) {
        return;
      }

      const newSelectedState: Record<string, File> = {};

      fileResponse.data?.items?.forEach((f) => {
        newSelectedState[f.fileId] = f;
      });

      setSelectedState(newSelectedState);
      formik.setValues({
        jobType: jobType ?? null,
        name: job.name ?? '',
      });
      setIsInitialized(true);
    },
    [jobSpec],
  );

  useEffect(() => {
    if (job && !isInitialized && jobSpec?.length) {
      initJob(job);
    }
  }, [job, isInitialized, jobSpec]);

  const mimetypes = useMemo(() => {
    if (formik.values.jobType) {
      return formik.values.jobType.inputs.map(({ mimeType }) => mimeType);
    }
    return null;
  }, [formik.values.jobType]);

  useEffect(() => {
    if (Object.values(selectedState).length > 0) {
      const newSelectedFileMimeTypes: string[] = [];
      mimetypes?.forEach((mimeType) => {
        let isSelected = (formik.values.jobType?.inputs.find((fs) => fs.mimeType === mimeType)?.dsRevision && (jsonOutput || job?.parameters.repositoryKey)) ||
          Object.values(selectedState).some((item) => item.mimeType === mimeType);
        if (!isSelected && !newSelectedFileMimeTypes.includes(mimeType)) {
          newSelectedFileMimeTypes.push(mimeType);
        }
      });
      setSelectedFileMimeTypes(newSelectedFileMimeTypes);
    }
  }, [selectedState]);

  useEffect(() => {
    const { skip, take } = pageOptions;

    const page = skip / take + 1;

    if (selectedMimeType) {
      const params = {
        page,
        take,
        mimetypes: [selectedMimeType],
        name: get(filter, 'filters[0].value', ''),
        status: 'Succeeded',
        sort: '',
      };
      const sortFilter = sort[0]?.field;
      if (sortFilter) {
        params.sort = `${sortFilter} ${sort[0].dir}`;
      }
      dispatch(fetchFiles(params));
    }
  }, [pageOptions, selectedMimeType, filter, sort]);

  useEffect(() => {
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
  }, [selectedMimeType]);

  const renderDate = ({ dataItem }: { dataItem: File }) => {
    return <td>{getFormattedDateTime(dataItem.creationDatetime)}</td>;
  };

  const renderName = ({ dataItem }: { dataItem: File }) => {
    return <td><TruncateText text={dataItem.name} /></td>;
  };

  const rowRender = (trElement: React.ReactElement, rowProps: any) => {
    const mimeTypeName = rowProps.dataItem.name;
    const isDbField = !!formik.values.jobType?.inputs.find((fs) => fs.mimeType === mimeTypeName)?.dsRevision;
    const isMimeTypeNotSelected = selectedFileMimeTypes.includes(mimeTypeName);

    let isFailedRow = false;

    if (isDbField) {
      const hasJsonOutput = !!jsonOutput?.length;
      const hasJobWithRepository = job && !!job?.parameters?.repositoryKey;

      if (!hasJsonOutput && !hasJobWithRepository && isMimeTypeNotSelected) {
        isFailedRow = true;
      }
    } else if (isMimeTypeNotSelected) {
      isFailedRow = true;
    }

    const rowClassName = classNames({
      'failed-row': isFailedRow,
    });

    return cloneElement(
      trElement,
      {
        ...trElement.props,
        className: rowClassName,
      },
      trElement.props.children,
    );
  };

  const filePageChange = (event: { page: { skip: number; take: number } }) => {
    const { skip, take } = event.page;
    setPageOptions({ skip, take });
  };

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const fileId = event.dataItem.fileId;
      const mimeType = event.dataItem.mimeType;
      const selectedJobType = formik.values.jobType;
      let max = 1;
      if (selectedJobType) {
        const input = selectedJobType.inputs.find((i) => i.mimeType === mimeType);
        max = input?.max ?? 1;
        if (input?.dsRevision && jsonOutput) {
          setJsonOutput('');
        }
      }

      const isSelected: boolean = (event.syntheticEvent.target as HTMLInputElement).value !== 'on';

      const newSelectedState = { ...selectedState };

      if (!isSelected) {
        const currentMimeTypeFiles = Object.values(selectedState).filter(
          (s) => s.mimeType === mimeType,
        );
        const [firstSelectedFile] = currentMimeTypeFiles;
        if (max === currentMimeTypeFiles.length && firstSelectedFile?.fileId) {
          delete newSelectedState[firstSelectedFile.fileId];
        }
      }

      if (isSelected) {
        delete newSelectedState[fileId];
      } else {
        newSelectedState[fileId] = event.dataItem;
      }
      setSelectedState(newSelectedState);
    },
    [selectedState],
  );

  mimetypesData = useMemo(() => {
    if (!mimetypes?.length) return [];
    const requiresDsImport = BOM_JOB_TYPES.has(formik.values.jobType?.id ?? '');
    return mimetypes
      .filter((m) => {
        if (!requiresDsImport) return true;
        return (fileSpec.items.find((fs) => fs.mimeType === m)?.key ?? '') !== REPOSITORY_COMMIT_FILE_KEY;
      })
      .map((m) => ({
      name: m,
      fileKey: fileSpec.items.find((fs) => fs.mimeType === m)?.key ?? '',
      purpose: fileSpec.items.find((fs) => fs.mimeType === m)?.name ?? '',
      actions: [
        {
          text: 'Select',
          onClick: (m: any) => {
            setSelectedMimeType(m.name);
          },
          icon: 'icon-ui-general-multi-select',
          visible: true,
        },
        {
          type: 'primary',
          icon: 'icon-ui-general-link',
          text: 'Link to Database',
          onClick: () => {
            setShowLinkToDatabaseModal(true);
          },
          visible: formik.values.jobType?.inputs.find((fs) => fs.mimeType === m)?.dsRevision ?? false,
          tooltip: 'Link to Database',
          position: '',
        },
      ].filter(({ visible }) => visible),
    }));
  }, [mimetypes, fileSpec, formik.values.jobType]);

  const renderSelectedFiles = ({ dataItem }: { dataItem: { fileKey: string, name: string } }) => {
    const mimeTypeName = dataItem.name;
    const isDbField = !!formik.values.jobType?.inputs.find((fs) => fs.mimeType === mimeTypeName)?.dsRevision;
    if (isDbField) {
      let linkTo: string | undefined;

      if (jsonOutput?.length) {
        const json = JSON.parse(jsonOutput);
        linkTo = `${dataServer?.url}/repositories/${json.repositoryKey}/${json.commitKey}`;
      } else if (job?.parameters?.repositoryKey) {
        linkTo = `${dataServer?.url}/repositories/${job.parameters.repositoryKey}/${job.parameters.commitKey}`;
      }

      if (linkTo) {
        return (
          <td>
            <Link to={linkTo} target='blank'>
              Database
            </Link>
          </td>
        );
      }
    }

    const selectedFileNames = Object.values(selectedState)
      .filter((s: { mimeType: string; name: string }) => s.mimeType === mimeTypeName)
      .map((s: { mimeType: string; name: string }) => s.name);

    const displayText = selectedFileNames.length > 0 ? selectedFileNames[0] : 'No file selected';

    return (
      <td>
        <TruncateText text={displayText} />
      </td>
    );
  };

  const mimeTypeGridData = mimetypesData.map((item) => ({
    ...item,
    _selectedFiles: (renderSelectedFiles({ dataItem: item as any }) as any)?.props?.children,
  }));

  const mimeTypeGridMetadata = [
    { field: 'purpose', title: 'Purpose of file' },
    { field: '', type: 'component', object: '_selectedFiles', title: 'Selected Files' },
  ];

  const mimeTypeGridActions = useMemo(
    () => [
      {
        text: 'Select',
        onClick: (m: any) => {
          setSelectedMimeType(m.name);
        },
        icon: 'icon-ui-general-multi-select',
      },
    ],
    [],
  );

  const tableData = useMemo(
    () => {
      return files.items.map((file) => ({
        ...file,
        selected: !!selectedState[file.fileId],
        id: file.fileId,
        _name: (renderName({ dataItem: file as any }) as any)?.props?.children,
        _uploadDate: (renderDate({ dataItem: file as any }) as any)?.props?.children,
      }));
    }, [files, selectedState, getFormattedDateTime],
  );

  const renderLoader = (className?: string) => (
    <div className={classNames('global-spinner', className)}>
      <SpheraSpinner version={2} color="dark-1" size="large" />
    </div>
  );

  const baseUrl = dataServer ? `${dataServer?.url}/api/v1.0` : '';
  const token = AuthHelper.getAccessToken() ?? '';

  const handleJsonOutputChange = (json: string) => {
    setJsonOutput(json);
  };

  const onDataStateChange = (e: any) => {
    setFilter(e.filter);
  };

  const isActionDisabled = useMemo(() => {
    const requiresDsImport = BOM_JOB_TYPES.has(formik.values.jobType?.id ?? '');
    const visibleMimetypes = requiresDsImport
      ? mimetypes?.filter((m) => (fileSpec.items.find((fs) => fs.mimeType === m)?.key ?? '') !== REPOSITORY_COMMIT_FILE_KEY)
      : mimetypes;
    return (actionDisabled || !formik.isValid) && !visibleMimetypes?.every((mimeType) =>
      (formik.values.jobType?.inputs.find((fs) => fs.mimeType === mimeType)?.dsRevision && (jsonOutput || job?.parameters.repositoryKey)) ||
      Object.values(selectedState).some((item) => item.mimeType === mimeType),
    );
  }, [actionDisabled, selectedState, formik.isValid, jsonOutput, job, mimetypes, fileSpec, formik.values.jobType]);

  const filesGridMetadata = [
    { field: 'name', title: 'Name', type: 'component', object: '_name', filterCell: 'text', sortable: true },
    { field: 'creationDatetime', type: 'component', object: '_uploadDate', title: 'Upload Date', sortable: true },
  ];

  const onTypeChange = async (e: any) => {
    await formik.setFieldValue('jobType', e?.value ?? null);
    setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    await formik.validateForm();
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    if (pageOptions.skip !== initialPageOptions.skip) {
      setPageOptions((prev) => ({ skip: 0, take: prev.take }));
    }
    setSort(e.sort as ISortDescriptor[]);
  };

  return (
    <>
      <SpheraModal
        className="create-job-modal"
        title="Add Job"
        height={height}
        width={width}
        useMaximizeButton={maximize}
        useMinimizeButton={minimize}
        actionText="Create Job"
        onMaximize={() => {
          setHeight('100%');
          setWidth('100%');
          setMaximize(false);
          setMinimize(true);
        }}
        onMinimize={() => {
          setHeight(BASIC_HEIGHT);
          setWidth(BASIC_WIDTH);
          setMaximize(true);
          setMinimize(false);
        }}
        onAction={() => {
          formik.submitForm();
        }}
        actionDisabled={isActionDisabled}
        onCancel={onClose}
        onClose={onClose}
      >
        <div className="create-job-modal__content inner-content p-4">
          <div className="row">
            <div className="col col-6">
              <SpheraInput
                label="Job Name"
                name="name"
                value={formik.values.name}
                onChange={async ({ value }: { value: string }) => {
                  await formik.setFieldValue('name', value);
                  await formik.validateForm();
                }}
                placeHolder="Name of Job"
                valid={!formik.errors.name}
                touched={formik.touched.name}
                validationMessage={formik.errors.name}
              />
            </div>
            <div className="col col-6">
              <SpheraDropdown
                data={jobSpecUi}
                textField="name"
                value={formik.values.jobType}
                name="jobType"
                valid={!formik.errors.jobType}
                touched={formik.touched.jobType}
                validationMessage={formik.errors.jobType}
                dataItemKey="id"
                label="Job Type"
                onChange={onTypeChange}
              />
            </div>
          </div>

          {isLoading && renderLoader()}
          {mimetypes?.length && (
            <Grid
              className="mt-3"
              height="200px"
              data={mimeTypeGridData}
              hidePagination
              rowRender={rowRender}
              metadata={mimeTypeGridMetadata}
              actions={mimeTypeGridActions}
            />
          )}
          {selectedMimeType && (
            <Grid
              serverSideData
              className="mt-3 sphera-grid"
              pageable
              selectable={{
                enabled: true,
                drag: false,
                cell: false,
                mode: 'single',
              }}
              selectedField="selected"
              data={tableData}
              total={files.totalCount || 0}
              onPageChange={filePageChange}
              onSelectionChange={(data: any) => onSelectionChange(data)}
              scrollable="scrollable"
              showSelectionCheckbox
              headerCellRender={(td: any, { field }: { field: string }) => {
                if (field === 'selected') return null;
                return td;
              }}
              style={{
                maxHeight: height === '100%' ? 510 : 420,
              }}
              filterable
              filter={filter}
              sort={sort}
              onDataStateChange={onDataStateChange}
              metadata={filesGridMetadata}
              onFilterChange={onDataStateChange}
              onSortChange={handleSortChange}
              pageOptions={PAGE_OPTIONS}
              {...pageOptions}
            />
          )}
        </div>

        <SpheraNotificationGroup
          position={{
            top: 70,
            right: 30,
            left: 30,
          }}
        >
          {notification.isOpen && (
            <SpheraNotification
              style={{
                zIndex: 10005,
              }}
              closable={true}
              type={notification.type}
              text={notification.text}
              onClose={() => setNotification(defaultNotification)}
            />
          )}
        </SpheraNotificationGroup>
      </SpheraModal>
      {baseUrl && (
        <LinkToRepo
          show={showLinkToDatabaseModal}
          onClose={() => setShowLinkToDatabaseModal(false)}
          url={baseUrl}
          token={token}
          className="inner-content"
          onJsonOutputChange={handleJsonOutputChange}
          getFormattedDateTime={getFormattedDateTime}
        />
      )}
    </>
  );
};

export default CreateJobModal;
